import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Paper, Text, Loader, Button } from '@mantine/core';
import { IconGripVertical, IconTarget, IconMapPin} from '@tabler/icons-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BASE_URL } from '../Redux/apiConfig';
// Add these imports at the top of your InteractiveMap.js file
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css"; 
import "leaflet-draw";

const InteractiveMap = forwardRef(({ 
  userAccountId, 
  focusedFieldIndex, 
  fieldData,
  setFocusedFieldIndex,
  onOpenCoordinateEditor,
  coordinateSelectionMode,
  onCoordinateSelect,
  selectionPointInfo,
  onAddFieldAtCenter,
  initialCenter,
  initialZoom,
  onViewChange
}, ref) => {
  const [farmFields, setFarmFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  
  // Add editing state
  const [editingMode, setEditingMode] = useState(true);
  const [editingShapes, setEditingShapes] = useState(new Map());
  const [pendingChanges, setPendingChanges] = useState(new Map());

  const [drawingMode, setDrawingMode] = useState(null);
  const [tempSelectionMarker, setTempSelectionMarker] = useState(null);
  const tempMarkerRef = useRef(null);
  // Ref to access coordinateSelectionMode without it being a dep of focusedFieldIndex effect
  const coordinateSelectionModeRef = useRef(coordinateSelectionMode);
  const [mapReady, setMapReady] = useState(false);
  const intl = useIntl();

   useEffect(() => {
    if (mapInstanceRef.current && coordinateSelectionMode) {
      const map = mapInstanceRef.current;
      
      // Leaflet's pane elements override container cursor, so inject a style tag
      const styleId = 'leaflet-crosshair-override';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          .leaflet-container.crosshair-mode,
          .leaflet-container.crosshair-mode .leaflet-grab,
          .leaflet-container.crosshair-mode .leaflet-interactive,
          .leaflet-container.crosshair-mode .leaflet-pane,
          .leaflet-container.crosshair-mode .leaflet-tile-pane,
          .leaflet-container.crosshair-mode .leaflet-map-pane {
            cursor: crosshair !important;
          }
        `;
        document.head.appendChild(style);
      }
      map.getContainer().classList.add('crosshair-mode');
      
      const handleCoordinateClick = (e) => {
        if (coordinateSelectionMode && onCoordinateSelect) {
          e.originalEvent.stopPropagation();
          e.originalEvent.preventDefault();
          
          const { lat, lng } = e.latlng;
          
          // Remove existing temp marker if any
          if (tempMarkerRef.current) {
            map.removeLayer(tempMarkerRef.current);
          }
          
          // Add temporary marker at clicked location
          const tempMarker = window.L.marker([lat, lng], {
            icon: window.L.divIcon({
              className: 'temp-selection-marker',
              html: `<div style="
                background: #ff6b6b;
                color: white;
                border-radius: 50%;
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 16px;
                font-weight: bold;
                border: 3px solid white;
                box-shadow: 0 3px 8px rgba(0,0,0,0.5);
              ">📍</div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12]
            }),
            zIndexOffset: 2000
          }).addTo(map);
          
          tempMarkerRef.current = tempMarker;
          setTempSelectionMarker(tempMarker);
          
          onCoordinateSelect(lat, lng);
          
          // Remove temp marker after coordinate is processed
          setTimeout(() => {
            if (tempMarkerRef.current) {
              map.removeLayer(tempMarkerRef.current);
              tempMarkerRef.current = null;
              setTempSelectionMarker(null);
            }
          }, 800);
        }
      };
      
      map.on('click', handleCoordinateClick);
      
      return () => {
        map.off('click', handleCoordinateClick);
        map.getContainer().classList.remove('crosshair-mode');
        
        if (tempMarkerRef.current) {
          map.removeLayer(tempMarkerRef.current);
          tempMarkerRef.current = null;
        }
      };
    }
  }, [coordinateSelectionMode, onCoordinateSelect, mapReady]);

  // Keep ref in sync so the focusedFieldIndex effect always reads the latest value
  // without needing coordinateSelectionMode as a dependency
  useEffect(() => {
    coordinateSelectionModeRef.current = coordinateSelectionMode;
  }, [coordinateSelectionMode]);

useEffect(() => {
  if (focusedFieldIndex !== null && mapInstanceRef.current && fieldData && fieldData[focusedFieldIndex]) {
    // CRITICAL: Add prevention check here too
    const shouldPreventMapReset = window.preventMapReset || 
                                  coordinateSelectionModeRef.current || 
                                  window.coordinateSelectionActive;
    
    if (shouldPreventMapReset) {
      return; // Exit early, don't change the map view
    }
    
    // Add safety check for map container
    const map = mapInstanceRef.current;
    if (!map || !map.getContainer() || !map._loaded) {
      console.warn('Map not ready for fitBounds operation');
      return;
    }
    
    const field = fieldData[focusedFieldIndex];
    const coords = field.gpsPoints?.map(point => [point.latitude, point.longitude]) || [];
    
    if (coords.length >= 1) {
      let bounds;
      
      try {
        if (coords.length >= 3) {
          const polygon = window.L.polygon(coords);
          bounds = polygon.getBounds();
        } else {
          bounds = window.L.latLngBounds(coords);
        }
        
        // Validate bounds before fitBounds
        if (bounds && bounds.isValid()) {
          mapInstanceRef.current.fitBounds(bounds, { 
            padding: [30, 30],
            maxZoom: 16
          });
        }
      } catch (error) {
        console.error('Error fitting bounds:', error);
      }
    }
  }
}, [focusedFieldIndex, fieldData]);
  
useEffect(() => {
  // Safety check: ensure map exists and is ready
  if (!mapInstanceRef.current || !mapInstanceRef.current.getContainer() || !mapInstanceRef.current._loaded) {
    return;
  }
  
  if (mapInstanceRef.current && fieldData) {
    // Check prevention flags to determine if we should prevent zoom/bounds changes
    const shouldPreventMapReset = window.preventMapReset || 
                                  coordinateSelectionMode || 
                                  window.coordinateSelectionActive;
    
    // DON'T exit early - we need to draw the shapes!
    // Just use the flag later to prevent zoom changes
    
    // Store current map view to preserve it
    const currentZoom = mapInstanceRef.current.getZoom();
    const currentCenter = mapInstanceRef.current.getCenter();
    
    // Determine which fields to update
    let fieldsToUpdate = [];
    
    if (focusedFieldIndex !== null && focusedFieldIndex < fieldData.length) {
      // Only update the focused field
      fieldsToUpdate = [focusedFieldIndex];

      // Debug: Count layers before removal
      let layersBeforeRemoval = 0;
      mapInstanceRef.current.eachLayer(() => layersBeforeRemoval++);
      
      // Remove only the focused field's existing shapes
      const existingShape = editingShapes.get(focusedFieldIndex);
      if (existingShape) {
        try {
          mapInstanceRef.current.removeLayer(existingShape);
        } catch (error) {
          console.error('Error removing existing shape:', error);
        }
      }
      
      // Debug ALL layers to see what's on them
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer.options && layer.options.isFieldShape) {
        }
      });
      
      // Remove layers by checking the HTML content for the field number
      const layersToRemove = [];
      mapInstanceRef.current.eachLayer((layer) => {
        const shouldRemove = (
          // Method 1: Normal fieldIndex check
          (layer.options && layer.options.isFieldShape && 
          layer.options.fieldIndex === focusedFieldIndex) ||
          // Method 2: Check HTML content for field number
          (layer.options && layer.options.isFieldShape && layer.options.icon?.options?.html && 
          layer.options.icon.options.html.includes(`>${focusedFieldIndex + 1}<`))
        );
        
        if (shouldRemove) {
          layersToRemove.push(layer);
        }
      });

      layersToRemove.forEach(layer => {
        try {
          mapInstanceRef.current.removeLayer(layer);
        } catch (error) {
          console.error('Error removing layer:', error);
        }
      });
    } else {
      // Update all fields (fallback to original behavior)
      fieldsToUpdate = fieldData.map((_, index) => index);
      
      // Clear all existing shapes
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer.options && (layer.options.isFieldShape || 
            layer.options.className === 'field-marker' || 
            layer.options.className === 'field-label-marker')) {
          try {
            mapInstanceRef.current.removeLayer(layer);
          } catch (error) {
            console.error('Error removing layer:', error);
          }
        }
      });
    }
    
    // Re-render specified fields with updated data
    const bounds = new window.L.LatLngBounds();
    const shapes = new Map(editingShapes); // Start with existing shapes
    
    fieldsToUpdate.forEach((index) => {
      const field = fieldData[index];
      
      // Better coordinate validation
      const coords = field.gpsPoints?.filter(point => 
        point && 
        point.latitude !== null && 
        point.latitude !== undefined && 
        point.longitude !== null && 
        point.longitude !== undefined &&
        !isNaN(point.latitude) && 
        !isNaN(point.longitude) &&
        Math.abs(point.latitude) <= 90 &&
        Math.abs(point.longitude) <= 180
      ).map(point => [parseFloat(point.latitude), parseFloat(point.longitude)]) || [];
      
      if (coords.length === 0) {
        // Remove from shapes map if it exists
        shapes.delete(index);
        return;
      }
      
      const createFieldClickHandler = (fieldIndex) => {
        return (e) => {
          // Don't stop propagation in coordinate selection mode so the map-level
          // click handler (handleCoordinateClick) can receive the event
          if (e && !coordinateSelectionMode && !window.coordinateSelectionActive) {
            e.stopPropagation();
            e.preventDefault();
          }
          
          if (!coordinateSelectionMode && !window.coordinateSelectionActive && setFocusedFieldIndex) {
            setFocusedFieldIndex(fieldIndex);
            if (onOpenCoordinateEditor) {
              onOpenCoordinateEditor(fieldData[fieldIndex], fieldIndex);
            }
          }
        };
      };
      
      const handleFieldClick = createFieldClickHandler(index);
      let shape = null;
      
      if (coords.length === 1) {
        const marker = window.L.marker(coords[0], {
          icon: window.L.divIcon({
            className: 'field-marker',
            html: `<div style="
              background: #20B2AA;
              color: white;
              border-radius: 50%;
              width: 28px;
              height: 28px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 14px;
              font-weight: bold;
              border: 3px solid white;
              box-shadow: 0 3px 6px rgba(0,0,0,0.4);
              cursor: pointer;
              pointer-events: auto;
              z-index: 1000;
            ">${index + 1}</div>`,
            iconSize: [28, 28],
            iconAnchor: [14, 14]
          }),
          isFieldShape: true,
          fieldIndex: index,
          zIndexOffset: 1000,
        }).addTo(mapInstanceRef.current);
        
        if (!shouldPreventMapReset) {
          bounds.extend(coords[0]);
        }
        marker.on('click', handleFieldClick);
        shape = marker;
        
      } else if (coords.length === 2) {
        const polyline = window.L.polyline(coords, {
          color: '#20B2AA',
          weight: 5,
          opacity: 0.8,
          dashArray: '5, 5',
          interactive: true,
          isFieldShape: true,
          fieldIndex: index
        }).addTo(mapInstanceRef.current);
        
        if (!shouldPreventMapReset) {
          bounds.extend(polyline.getBounds());
        }
        polyline.on('click', handleFieldClick);
        shape = polyline;
        
        // Add numbered marker for polyline (2 points)
        const midpoint = window.L.latLng(
          (coords[0][0] + coords[1][0]) / 2,
          (coords[0][1] + coords[1][1]) / 2
        );
        const labelMarker = window.L.marker(midpoint, {
          icon: window.L.divIcon({
            className: 'field-label-marker',
            html: `<div style="
              background: white;
              padding: 6px 10px;
              border-radius: 6px;
              font-size: 14px;
              font-weight: bold;
              color: black;
              border: 3px solid #20B2AA;
              white-space: nowrap;
              cursor: pointer;
              pointer-events: auto;
              box-shadow: 0 3px 6px rgba(0,0,0,0.4);
              z-index: 1000;
            ">${index + 1}</div>`,
            iconSize: [35, 25],
            iconAnchor: [17, 12]
          }),
          isFieldShape: true,
          fieldIndex: index, 
          zIndexOffset: 1000,
        }).addTo(mapInstanceRef.current);
        
        labelMarker.on('click', handleFieldClick);
        
      } else if (coords.length >= 3) {
        const polygon = window.L.polygon(coords, {
          color: '#20B2AA',
          weight: 3,
          fillColor: '#20B2AA',
          fillOpacity: 0.4,
          opacity: 0.9,
          interactive: true,
          isFieldShape: true,
          fieldIndex: index // Add field index for identification
        }).addTo(mapInstanceRef.current);
        
        if (polygon.getBounds().isValid() && !shouldPreventMapReset) {
          bounds.extend(polygon.getBounds());
        }
        
        polygon.on('click', handleFieldClick);
        shape = polygon;
        
        // Add label
        const center = polygon.getBounds().getCenter();
        const labelMarker = window.L.marker(center, {
          icon: window.L.divIcon({
            className: 'field-label-marker',
            html: `<div style="
              background: white;
              padding: 6px 10px;
              border-radius: 6px;
              font-size: 14px;
              font-weight: bold;
              color: black;
              border: 3px solid #20B2AA;
              white-space: nowrap;
              cursor: pointer;
              pointer-events: auto;
              box-shadow: 0 3px 6px rgba(0,0,0,0.4);
              z-index: 1000;
            ">${index + 1}</div>`,
            iconSize: [35, 25],
            iconAnchor: [17, 12]
          }),
          isFieldShape: true,
          fieldIndex: index, 
          zIndexOffset: 1000,
        }).addTo(mapInstanceRef.current);
        
        labelMarker.on('click', handleFieldClick);

        // ADD THIS: Point markers at each GPS coordinate
        coords.forEach((coord, pointIndex) => {
          const pointMarker = window.L.marker(coord, {
            icon: window.L.divIcon({
              className: 'point-marker',
              html: `<div style="
                       color: black;
                  font-size: 12px;
                  font-weight: bold;
                  background: none;
                  border: none;
                  width: auto;
                  height: auto;
                  text-shadow: 1px 1px 1px white, -1px -1px 1px white, 1px -1px 1px white, -1px 1px 1px white;
              ">${pointIndex + 1}</div>`,
              iconSize: [18, 18],
              fieldIndex: index,
              iconAnchor: [9, 9]
            }),
            isFieldShape: true,
            fieldIndex: index,
            zIndexOffset: 999,
          }).addTo(mapInstanceRef.current);
          
          pointMarker.on('click', handleFieldClick);
        });

      }
      
      if (shape) {
        shapes.set(index, shape);
      }
    });
    
    setEditingShapes(shapes);
    
    // Bounds and zoom logic
    if (shouldPreventMapReset || window.coordinateEditorActive) {
    } else if (bounds.isValid()) {
      // Only fit bounds if we're updating all fields or if it's initial load
      if (focusedFieldIndex === null) {
        // Normal bounds fitting logic for non-protected operations
        const isInitialLoad = !window.mapHasBeenInitialized;
        const hasMultipleFields = fieldData.length > 1;
        const timeSinceLastInteraction = Date.now() - (window.lastMapInteraction || 0);
        const userRecentlyInteracted = timeSinceLastInteraction < 5000;
        
        // Uncomment and modify as needed for your bounds fitting logic
        // if (isInitialLoad || (hasMultipleFields && !userRecentlyInteracted)) {
        //   setTimeout(() => {
        //     if (mapInstanceRef.current && mapInstanceRef.current.getContainer()) {
        //       mapInstanceRef.current.fitBounds(bounds, { 
        //         padding: [10, 10],
        //         maxZoom: 16 
        //       });
        //       window.mapHasBeenInitialized = true;
        //     }
        //   }, 100);
        // }
      }
    }
    
    // Track user interactions to prevent unwanted zoom resets
    if (mapInstanceRef.current && !mapInstanceRef.current._userInteractionTracker) {
      mapInstanceRef.current._userInteractionTracker = true;
      
      ['zoomstart', 'dragstart', 'zoomend', 'dragend', 'click'].forEach(eventName => {
        mapInstanceRef.current.on(eventName, () => {
          window.lastMapInteraction = Date.now();
        });
      });
    }
  }
}, [fieldData, coordinateSelectionMode, window.coordinateSelectionActive, focusedFieldIndex]); // Added focusedFieldIndex dependency

  useImperativeHandle(ref, () => ({
    mapInstanceRef,
  }));

  useEffect(() => {
    const loadLeaflet = async () => {
      if (window.L) {
        setLeafletLoaded(true);
        return;
      }
      
      try {
        // Load Leaflet CSS
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const cssLink = document.createElement('link');
          cssLink.rel = 'stylesheet';
          cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
          cssLink.crossOrigin = '';
          document.head.appendChild(cssLink);
        }

        // Load Leaflet JS first
        if (!window.L) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
            script.crossOrigin = '';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

      } catch (err) {
        console.error('Error loading Leaflet:', err);
        setError('Failed to load map library');
      }
    };

    loadLeaflet();
  }, []);

  useEffect(() => {
    const fetchMapData = async () => {
      if (!userAccountId) return;
      
      try {
        setLoading(true);
        const authToken = localStorage.getItem('authToken');
        
        const response = await fetch(`${BASE_URL}/getField`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
            apikey: 'abc123'
          },
          body: JSON.stringify({ accountId: userAccountId })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch farm data: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.status === 'Success' && data.result) {
          setFarmFields(data.result);
        } else {
          setFarmFields([]);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching map data:', err);
        // Don't show error if we have fieldData from parent (MyFarm component)
        if (!fieldData || fieldData.length === 0) {
          setError(`Failed to load map data: ${err.message}`);
        }
        setFarmFields([]);
      } finally {
        setLoading(false);
      }
    };

    // Skip fetching if in coordinate selection mode or if we have fieldData from parent
    if (coordinateSelectionMode) {
      setLoading(false);
      return;
    }

    // Only fetch if we don't have fieldData from parent
    if (!fieldData || fieldData.length === 0) {
      fetchMapData();
    } else {
      setLoading(false);
    }
  }, [userAccountId, fieldData, coordinateSelectionMode]);

  useEffect(() => {
    const fieldsToUse = fieldData && fieldData.length > 0 ? fieldData : farmFields;
    
    // CRITICAL: Don't reinitialize map when we're in the middle of an operation
    // BUT allow initialization if we're in coordinateSelectionMode
    if (!coordinateSelectionModeRef.current && (window.preventMapReset || window.coordinateSelectionActive)) {
      return;
    }
    
    // In coordinate selection mode, we don't need fields to initialize the map
    const shouldInitialize = coordinateSelectionModeRef.current || fieldsToUse.length > 0;
    
    if (leafletLoaded && !loading && shouldInitialize && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
    
    return () => {
      if (mapInstanceRef.current && !window.preventMapReset && !coordinateSelectionModeRef.current && !window.coordinateSelectionActive) {
        try {
          mapInstanceRef.current.remove();
        } catch (error) {
          console.error('Error removing map:', error);
        } finally {
          mapInstanceRef.current = null;
          setMapReady(false);
        }
      }
    };
  }, [leafletLoaded, loading, farmFields.length, fieldData.length]);

  // 6. Add useEffect to update control buttons when state changes:
  useEffect(() => {
    if (mapInstanceRef.current) {
      // Update button states
      const polygonBtn = document.getElementById('draw-polygon');
      const polylineBtn = document.getElementById('draw-polyline');
      const markerBtn = document.getElementById('draw-marker');
      const editBtn = document.getElementById('toggle-editing');
      const saveBtn = document.getElementById('save-changes');
      const instructions = document.querySelector('.drawing-instructions > div');
      
      if (polygonBtn) {
        polygonBtn.style.background = drawingMode === 'polygon' ? '#ff6b6b' : '#20B2AA';
        polygonBtn.textContent = drawingMode === 'polygon' ? 'Cancel Polygon' : 'Draw Polygon';
      }
      
      if (polylineBtn) {
        polylineBtn.style.background = drawingMode === 'polyline' ? '#ff6b6b' : '#20B2AA';
        polylineBtn.textContent = drawingMode === 'polyline' ? 'Cancel Line' : 'Draw Line';
      }
      
      if (markerBtn) {
        markerBtn.style.background = drawingMode === 'marker' ? '#ff6b6b' : '#20B2AA';
        markerBtn.textContent = drawingMode === 'marker' ? 'Cancel Marker' : 'Draw Marker';
      }
      
      if (editBtn) {
        editBtn.style.background = editingMode ? '#ff6b6b' : '#20B2AA';
        editBtn.textContent = editingMode ? 'Stop Editing' : 'Edit Mode';
      }
      
      if (saveBtn) {
        saveBtn.style.display = pendingChanges.size > 0 ? 'block' : 'none';
        saveBtn.textContent = `Save (${pendingChanges.size})`;
      }
      
      if (instructions) {
        instructions.style.display = drawingMode ? 'block' : 'none';
        const content = instructions.querySelector('strong').nextSibling;
        if (drawingMode === 'polygon') {
          content.textContent = 'Click to add points, double-click to finish';
        } else if (drawingMode === 'polyline') {
          content.textContent = 'Click to add points, double-click to finish';
        } else if (drawingMode === 'marker') {
          content.textContent = 'Click anywhere to place marker';
        }
      }
    }
  }, [drawingMode, editingMode, pendingChanges.size]);

  const initializeMap = () => {
    if (!window.L || !mapRef.current || mapInstanceRef.current) return;
    
    // CRITICAL: Don't initialize map when prevention flags are set
    // BUT allow initialization if we're in coordinateSelectionMode (SignUpFormPage)
    if (!coordinateSelectionMode && (window.preventMapReset || window.coordinateSelectionActive)) {
      return;
    }
    
    // Additional safety check: ensure map container element exists and is mounted
    if (!mapRef.current || !mapRef.current.parentElement) {
      console.warn('Map container not ready or not mounted');
      return;
    }

    // For coordinate selection mode, use default center and skip field rendering
    let centerLat, centerLng, zoom;
    const fieldsToUse = fieldData && fieldData.length > 0 ? fieldData : farmFields;
    
    if (coordinateSelectionMode) {
      // Use provided initial center/zoom or defaults for coordinate selection
      if (initialCenter && initialZoom !== undefined) {
        centerLat = initialCenter[0];
        centerLng = initialCenter[1];
        zoom = initialZoom;
      } else {
        centerLat = 20;
        centerLng = 0;
        zoom = 2;
      }
    } else {
      // Normal mode: require fields
      if (!fieldsToUse || fieldsToUse.length === 0) return;
      
      const firstField = fieldsToUse[0];
      const firstPoint = firstField.gpsPoints?.[0];
      if (!firstPoint) return;
      
      centerLat = firstPoint.latitude;
      centerLng = firstPoint.longitude;
      zoom = 12;
    }

    try {
      // Initialize map with editable: true
      const map = L.map(mapRef.current, {
        center: [centerLat, centerLng],
        zoom: zoom,
        zoomControl: true,
        attributionControl: false,
        doubleClickZoom: false,
        dragging: true,
        scrollWheelZoom: true,
        touchZoom: true,
        boxZoom: true,
        keyboard: true,
        preferCanvas: true // Better performance and stability
      });

      mapInstanceRef.current = map;
      setMapReady(true);

      // Track view changes and call callback if provided
      if (coordinateSelectionMode && onViewChange) {
        map.on('moveend', () => {
          const center = map.getCenter();
          const zoom = map.getZoom();
          onViewChange({ center: [center.lat, center.lng], zoom });
        });
      }

      // Define base layers
      const satelliteLayer = window.L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Esri',
        maxZoom: 19
      });

      const topoLayer = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      });

      // Add default layer (satellite)
      satelliteLayer.addTo(map);

      // Create layer control with translations
      const baseMaps = {
        [intl.formatMessage({ id: 'map.layer.satellite' })]: satelliteLayer,
        [intl.formatMessage({ id: 'map.layer.topographic' })]: topoLayer
      };

      window.L.control.layers(baseMaps, null, {
        position: 'topright'
      }).addTo(map);

      // Wait for map to be fully loaded before continuing
      map.whenReady(() => {
        console.log('Map is ready');
      });

      // Skip field rendering in coordinate selection mode
      if (!coordinateSelectionMode) {
        const bounds = new window.L.LatLngBounds();
        const shapes = new Map();

        // FIXED: Define click handler factory OUTSIDE the forEach loop
        const createFieldClickHandler = (fieldIndex) => {
        return (e) => {
          
          // Don't stop propagation in coordinate selection mode so the map-level
          // click handler (handleCoordinateClick) can receive the event
          if (e && !window.coordinateSelectionActive) {
            e.stopPropagation();
            e.preventDefault();
          }
          
          // Only handle if not in drawing or coordinate selection mode
          if (!drawingMode && !window.coordinateSelectionActive) {
            if (setFocusedFieldIndex) {
              setFocusedFieldIndex(fieldIndex);

              if (onOpenCoordinateEditor) {
                onOpenCoordinateEditor(fieldsToUse[fieldIndex], fieldIndex);
              }
            }
          }
        };
      };

      fieldsToUse.forEach((field, index) => {
        // Better coordinate validation
        const coords = field.gpsPoints?.filter(point => 
          point && 
          point.latitude !== null && 
          point.latitude !== undefined && 
          point.longitude !== null && 
          point.longitude !== undefined &&
          !isNaN(point.latitude) && 
          !isNaN(point.longitude) &&
          Math.abs(point.latitude) <= 90 &&
          Math.abs(point.longitude) <= 180
        ).map(point => [parseFloat(point.latitude), parseFloat(point.longitude)]) || [];

        // Skip if no valid coordinates
        if (coords.length === 0) {
          
          // Create a placeholder marker at map center for fields without coordinates
          if (mapInstanceRef.current) {
            const mapCenter = mapInstanceRef.current.getCenter();
            const placeholderMarker = window.L.marker([mapCenter.lat, mapCenter.lng], {
              icon: window.L.divIcon({
                className: 'field-marker-placeholder',
                html: `<div style="
                  background: #ff6b6b;
                  color: white;
                  border-radius: 50%;
                  width: 28px;
                  height: 28px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 14px;
                  font-weight: bold;
                  border: 3px solid white;
                  box-shadow: 0 3px 6px rgba(0,0,0,0.4);
                  cursor: pointer;
                  pointer-events: auto;
                  z-index: 1000;
                  opacity: 0.7;
                ">${index + 1}</div>`,
                iconSize: [28, 28],
                iconAnchor: [14, 14]
              }),
              isFieldShape: true
            }).addTo(mapInstanceRef.current);
            
            placeholderMarker.bindTooltip(`${field.name} - ${intl.formatMessage({ id: "map.no.coordinates" })}`, {
              permanent: false,
              direction: 'top'
            });
            
            placeholderMarker.on('click', createFieldClickHandler(index));
            shapes.set(index, placeholderMarker);
          }
          return;
        }
        
        // FIXED: Create the handler with the correct index
        const handleFieldClick = createFieldClickHandler(index);

        let shape = null;

        if (coords.length === 1) {
          const marker = window.L.marker(coords[0], {
            icon: window.L.divIcon({
              className: 'field-marker',
              html: `<div style="
                background: #20B2AA;
                color: white;
                border-radius: 50%;
                width: 28px;
                height: 28px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                font-weight: bold;
                border: 3px solid white;
                box-shadow: 0 3px 6px rgba(0,0,0,0.4);
                cursor: pointer;
                pointer-events: auto;
                z-index: 1000;
              ">${index + 1}</div>`,
              iconSize: [28, 28],
              iconAnchor: [14, 14]
            }),
            isFieldShape: true,
            zIndexOffset: 1000,
          }).addTo(map);

          bounds.extend(coords[0]);
          marker.on('click', handleFieldClick);
          shape = marker;

        } else if (coords.length === 2) {
          const polyline = window.L.polyline(coords, {
            color: '#20B2AA',
            weight: 5,
            opacity: 0.8,
            dashArray: '5, 5',
            interactive: true,
            bubblingMouseEvents: false
          }).addTo(map);

          bounds.extend(polyline.getBounds());
          polyline.on('click', handleFieldClick);
          
          shape = polyline;
          
          // Add numbered marker for polyline (2 points)
          const midpoint = window.L.latLng(
            (coords[0][0] + coords[1][0]) / 2,
            (coords[0][1] + coords[1][1]) / 2
          );
          const labelMarker = window.L.marker(midpoint, {
            icon: window.L.divIcon({
              className: 'field-label-marker',
              html: `<div style="
                background: white;
                padding: 6px 10px;
                border-radius: 6px;
                font-size: 14px;
                font-weight: bold;
                color: black;
                border: 3px solid #20B2AA;
                white-space: nowrap;
                cursor: pointer;
                pointer-events: auto;
                box-shadow: 0 3px 6px rgba(0,0,0,0.4);
                z-index: 1000;
              ">${index + 1}</div>`,
              iconSize: [35, 25],
              iconAnchor: [17, 12]
            }),
            isFieldShape: true,
            zIndexOffset: 1000,
          }).addTo(map);
          
          labelMarker.on('click', handleFieldClick);

        } else if (coords.length >= 3) {
          const polygon = window.L.polygon(coords, {
            color: '#20B2AA',
            weight: 3,
            fillColor: '#20B2AA',
            fillOpacity: 0.4,
            opacity: 0.9,
            interactive: true
          }).addTo(map);

          if (polygon.getBounds().isValid()) {
            bounds.extend(polygon.getBounds());
          }

          polygon.on('click', handleFieldClick);
          
          shape = polygon;

          // Add label marker
          const center = polygon.getBounds().getCenter();
          const labelMarker = window.L.marker(center, {
            icon: window.L.divIcon({
              className: 'field-label-marker',
              html: `<div style="
                background: white;
                padding: 6px 10px;
                border-radius: 6px;
                font-size: 14px;
                font-weight: bold;
                color: black;
                border: 3px solid #20B2AA;
                white-space: nowrap;
                cursor: pointer;
                pointer-events: auto;
                box-shadow: 0 3px 6px rgba(0,0,0,0.4);
                z-index: 1000;
              ">${index + 1}</div>`,
              iconSize: [35, 25],
              iconAnchor: [17, 12]
            }),
            isFieldShape: true,
            zIndexOffset: 1000,
          }).addTo(map);

          labelMarker.on('click', handleFieldClick);
          
        }

        // Store shape reference
        if (shape) {
          shapes.set(index, shape);
        }
      });

      // Store shapes for later reference
      setEditingShapes(shapes);

      // Fit bounds with safety check and delay
      if (bounds.isValid()) {
        try {
          // Give map time to fully initialize before fitting bounds
          setTimeout(() => {
            if (map && map.getContainer() && map._loaded && !map._removed) {
              try {
                // Additional check for map panes before fitBounds
                const mapPane = map.getPane('mapPane');
                if (mapPane && mapPane._leaflet_pos !== undefined) {
                  map.fitBounds(bounds, { padding: [10, 10], animate: false });
                }
              } catch (innerError) {
                console.warn('Map not ready for fitBounds, skipping:', innerError);
              }
            }
          }, 150);
        } catch (error) {
          console.error('Error fitting bounds:', error);
        }
      }

      shapes.forEach((shape, index) => {
      });
      } // End of !coordinateSelectionMode block

      // Add basic map click handler for debugging
      map.on('click', (e) => {
      });

    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to initialize map');
    }
  };

  if (loading || !leafletLoaded) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '300px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Loader color="teal" size="md" />
        <Text color="dimmed">
          <FormattedMessage id={loading ? "map.loading.fields" : "map.loading.maps"} />
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '300px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Text color="red" size="sm">{error}</Text>
        <Text color="dimmed" size="xs">
          <FormattedMessage id="map.error.refresh" />
        </Text>
      </div>
    );
  }

  if (farmFields.length === 0 && !coordinateSelectionMode) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '300px',
        flexDirection: 'column',
        gap: '16px'
      }}>
      <Text color="dimmed">
        <FormattedMessage id="map.no.fields" />
      </Text>
      <Text size="xs" color="dimmed">
        <FormattedMessage id="map.add.fields.hint" />
      </Text>
      </div>
    );
  }

  const fieldsToUse = fieldData && fieldData.length > 0 ? fieldData : farmFields;

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      {/* Add custom scrollbar styles for webkit browsers */}
      <style>{`
        .map-field-list-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .map-field-list-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .map-field-list-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.3);
          border-radius: 3px;
        }
        .map-field-list-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.5);
        }
      `}</style>
      <div 
        ref={mapRef} 
        style={{ 
          height: '100%', 
          width: '100%',
          borderRadius: '4px'
        }} 
      />
        {coordinateSelectionMode && selectionPointInfo && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',        // Dark semi-transparent
            color: 'white',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 400,
            zIndex: 1001,
            display: 'inline-flex',                       // Changed to inline-flex
            alignItems: 'center',
            gap: '6px',
            height: 'auto',                               // Explicit height control
            width: 'auto',                                // Explicit width control
            whiteSpace: 'nowrap'                          // Prevent wrapping
          }}>
            <IconTarget size={14} />
            <span>
              <FormattedMessage id="map.point.number" />{selectionPointInfo.pointIndex + 1}
            </span>
            <button
              onClick={() => onCoordinateSelect(null, null)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',  // Very subtle
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                borderRadius: '3px',
                padding: '2px 6px',
                cursor: 'pointer',
                fontSize: '11px',
                height: '20px',                           // Fixed height
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
        )}
      
      {/* Field list and add button - hide ONLY in coordinate selection mode (signup form) */}
      {!coordinateSelectionMode && (
        <>
      <div style={{
        position: 'absolute',
        top: '100px',
        left: '10px',
        maxWidth: '20%',
        maxHeight: '300px',
        overflowY: 'auto',
        zIndex: 1000,
        width: 'auto',
        height: 'auto',
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        borderRadius: '6px',
        padding: '4px',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0, 0, 0, 0.3) transparent'
      }}
      className="map-field-list-scroll"
      >
        {fieldsToUse.map((field, index) => (
          <div
            key={index}
            style={{
              padding: '6px',
              margin: '1px 0',
              backgroundColor: 'transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '400',
              borderLeft: pendingChanges.has(index) ? '3px solid #51cf66' : 'none'
            }}
            onClick={() => {
              // CLEAR ALL EDITING STATES FIRST
              window.preventMapReset = false;
              window.coordinateSelectionActive = false;
              window.coordinateEditorActive = false;
              if (setFocusedFieldIndex) {
                setFocusedFieldIndex(index);
              }
              if (onOpenCoordinateEditor) {
                onOpenCoordinateEditor(fieldsToUse[index], index);
              }
            }}
          >
            <div style={{fontWeight:'bold'}}>{index + 1}. {field.name} {pendingChanges.has(index) && '*'}</div>
              <div style={{fontSize:'11px', color:'#20B2AA'}}>
                {field.product ? (
                  <>
                    <FormattedMessage id="map.field.product" />
                    {field.product}
                  </>
                ) : ''}
                {field.region ? (
                  <>
                    <FormattedMessage id="map.field.region" />
                    {field.region}
                  </>
                ) : ''}
              </div>
          </div>
        ))}
      </div>
      <div style={{
        position: 'absolute',
        top: '420px', // Position it below the field list
        left: '10px',
        zIndex: 1000,
        width: 'auto',
        height: 'auto'
      }}>
        <Button 
          color="teal"
          size="sm"
          onClick={onAddFieldAtCenter}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)', // Semi-transparent teal
            border: '1px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(4px)',
            fontSize: '12px',
            padding: '8px 12px'
          }}
          leftIcon={<IconMapPin size={14} />}
        >
          <FormattedMessage id="button.add.farm" />
        </Button>
      </div>
        </>
      )}
    </div>
  );
});

// Updated ResizableMapSection that can use either iframe or interactive map
const ResizableMapSection = ({ 
  mapurl, 
  userAccountId, 
  useInteractiveMap = false, 
  focusedFieldIndex,
  fieldData,
  setFocusedFieldIndex,
  onOpenCoordinateEditor,
  coordinateSelectionMode,
  onCoordinateSelect,
  selectionPointInfo,
  onAddFieldAtCenter,
  mapComponentRef 
}) => {
  const [mapHeight, setMapHeight] = useState(500);
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(400);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    startYRef.current = e.clientY;
    startHeightRef.current = mapHeight;
    
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const deltaY = e.clientY - startYRef.current;
    const newHeight = Math.max(200, Math.min(800, startHeightRef.current + deltaY));
    setMapHeight(newHeight);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (window.L && mapComponentRef.current?.mapInstanceRef?.current) {
        mapComponentRef.current.mapInstanceRef.current.invalidateSize();
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, [mapHeight]);

  return (
    <Paper 
      padding="md" 
      shadow="xs" 
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: `${mapHeight}px`,
        minHeight: '200px',
        maxHeight: '800px',
        position: 'relative',
        transition: isDragging ? 'none' : 'height 0.1s ease'
      }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        flex: 1,
        overflow: 'hidden'
      }}>
        {useInteractiveMap ? (
          <div style={{ width: '95%', height: '95%' }}>
            <InteractiveMap
              ref={mapComponentRef} 
              userAccountId={userAccountId}
              focusedFieldIndex={focusedFieldIndex}
              fieldData={fieldData}
              setFocusedFieldIndex={setFocusedFieldIndex}
              onOpenCoordinateEditor={onOpenCoordinateEditor} 
              coordinateSelectionMode={coordinateSelectionMode}
              onCoordinateSelect={onCoordinateSelect}
              selectionPointInfo={selectionPointInfo}
              onAddFieldAtCenter={onAddFieldAtCenter}
            />
          </div>
        ) : (
          <iframe 
            src={mapurl}
            style={{ 
              width: '95%', 
              height: '95%', 
              border: 'none',
              borderRadius: '4px'
            }} 
            title="Farm Fields Map"
          />
        )}
      </div>
      
      {/* Resize Handle */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '12px',
          cursor: 'ns-resize',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: isDragging ? 'rgba(0, 123, 191, 0.1)' : 'transparent',
          borderTop: isDragging ? '1px solid #007bbf' : '1px solid transparent',
          transition: 'background-color 0.2s ease, border-color 0.2s ease'
        }}
        onMouseDown={handleMouseDown}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2px 8px',
          borderRadius: '4px',
          backgroundColor: isDragging ? 'rgba(0, 123, 191, 0.2)' : 'rgba(134, 142, 150, 0.1)',
          transition: 'background-color 0.2s ease'
        }}>
          <IconGripVertical size={14} color="#868e96" />
        </div>
      </div>
      
      {/* Height indicator when dragging */}
      {isDragging && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '15px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 500,
          pointerEvents: 'none',
          zIndex: 10
        }}>
          {mapHeight}px
        </div>
      )}
    </Paper>
  );
};

export { InteractiveMap, ResizableMapSection};